import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface BacklogRow {
  readonly backlogId: string;
  readonly backlogCode: string;
  readonly itemTitle: string;
  readonly itemType: string;
  readonly priority: string;
  readonly rank: number | null;
  readonly status: string;
}

const TYPES = [
  '', 'EPIC', 'FEATURE', 'ENHANCEMENT', 'TECHNICAL_DEBT',
  'COMPLIANCE_ITEM', 'DEFECT', 'IMPROVEMENT',
];
const STATUSES = ['', 'NEW', 'GROOMED', 'READY', 'IN_ROADMAP', 'DONE', 'ARCHIVED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M33 — Product Backlog Dashboard (§31 / §15). Ranked backlog of items (T439)
 * with item type, priority band, rank and the new→groomed→ready→roadmap→done
 * grooming lifecycle. Supports creating a backlog item and driving it — rank,
 * groom, ready, add-to-roadmap, complete and archive — each wired to the guarded
 * /v1/pm/backlog write API. Filterable by item type and status.
 */
export function ProductBacklogDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<BacklogRow[]>([]);
  const [itemType, setItemType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('FEATURE');
  const [newPriority, setNewPriority] = useState('MEDIUM');
  const [ranks, setRanks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (itemType) params.set('itemType', itemType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: BacklogRow[] }>(`/v1/pm/backlog${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, itemType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.backlog.notAuthorised')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m33.backlog.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/backlog`, {
        method: 'POST',
        body: { itemTitle: newTitle.trim(), itemType: newType, priority: newPriority },
        token,
      });
      setNotice(t('m33.backlog.created', { name: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: BacklogRow, path: string, label: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/backlog/${r.backlogId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m33.common.notice', { code: r.backlogCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="product-backlog-dashboard">
      <h1>{t('m33.backlog.title')}</h1>

      <section aria-label="create-backlog-item">
        <h2>{t('m33.backlog.createHeading')}</h2>
        <form aria-label="backlog-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.title')}<input aria-label="field-item-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m33.common.type')}
            <select aria-label="field-item-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m33.common.priority')}
            <select aria-label="field-item-priority" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-item" disabled={busy}>{busy ? t('m33.backlog.creating') : t('m33.backlog.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="backlog-error">{actionError}</p>}
        {notice && <p aria-label="backlog-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.type')}
          <select aria-label="filter-item-type" value={itemType} onChange={(e) => setItemType(e.target.value)}>
            {TYPES.map((ty) => <option key={ty || 'all'} value={ty}>{ty || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="backlog-count">{t('m33.backlog.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="backlog-table"
            rows={rows}
            rowKey={(r) => r.backlogId}
            columns={[
              { key: 'backlogCode', header: t('m33.common.code'), render: (r) => r.backlogCode },
              { key: 'itemTitle', header: t('m33.common.title'), render: (r) => r.itemTitle },
              { key: 'itemType', header: t('m33.common.type'), render: (r) => r.itemType },
              { key: 'priority', header: t('m33.common.priority'), render: (r) => r.priority },
              { key: 'rank', header: t('m33.backlog.colRank'), render: (r) => r.rank ?? '—' },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  <input aria-label={`rank-input-${r.backlogCode}`} type="number" value={ranks[r.backlogId] ?? ''} onChange={(e) => setRanks((m) => ({ ...m, [r.backlogId]: e.target.value }))} placeholder={t('m33.backlog.rankPh')} />
                  <Button variant="secondary" type="button" aria-label={`rank-${r.backlogCode}`} onClick={() => void act(r, 'rank', t('m33.backlog.act.ranked'), { rank: Number(ranks[r.backlogId] ?? 0) })}>{t('m33.backlog.btnRank')}</Button>
                  {r.status === 'NEW' && (
                    <Button variant="secondary" type="button" aria-label={`groom-${r.backlogCode}`} onClick={() => void act(r, 'groom', t('m33.backlog.act.groomed'))}>{t('m33.backlog.btnGroom')}</Button>
                  )}
                  {r.status === 'GROOMED' && (
                    <Button variant="secondary" type="button" aria-label={`ready-${r.backlogCode}`} onClick={() => void act(r, 'ready', t('m33.backlog.act.markedReady'))}>{t('m33.backlog.btnReady')}</Button>
                  )}
                  {r.status === 'READY' && (
                    <Button variant="primary" type="button" aria-label={`add-to-roadmap-${r.backlogCode}`} onClick={() => void act(r, 'add-to-roadmap', t('m33.backlog.act.addedToRoadmap'))}>{t('m33.backlog.btnAddToRoadmap')}</Button>
                  )}
                  {r.status === 'IN_ROADMAP' && (
                    <Button variant="secondary" type="button" aria-label={`complete-${r.backlogCode}`} onClick={() => void act(r, 'complete', t('m33.backlog.act.completed'))}>{t('m33.backlog.btnComplete')}</Button>
                  )}
                  {!['ARCHIVED', 'DONE'].includes(r.status) && (
                    <Button variant="danger" type="button" aria-label={`archive-${r.backlogCode}`} onClick={() => void act(r, 'archive', t('m33.backlog.act.archived'))}>{t('m33.backlog.btnArchive')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
